// This file will be auto-generated by TanStack Router
// For now, we'll create a basic route tree manually

import { createRootRoute, createRoute } from '@tanstack/react-router';
import { RootLayout } from './layouts/RootLayout';
import { AppLayout } from './layouts/AppLayout';
import { AuthPage } from './routes/auth';
import { SSOCallbackPage } from './routes/auth-sso-callback';
import { HomePage } from './routes/home';
import { ChatPage } from './routes/chat';
import { NotesPage } from './routes/notes';
import { PromptsPage } from './routes/prompts';
import { LandingPage } from './routes/landing';
import { PrivacyPage } from './routes/privacy';
import { TermsPage } from './routes/terms';
import { DeleteAccountPage } from './routes/delete-account';
import { PricingPage } from './routes/pricing';
import { BillingPage } from './routes/billing';
import { AdminLayout } from './components/admin/AdminLayout';
import { RouteErrorFallback } from './components/system/RouteErrorFallback';
import { ChatErrorFallback } from './components/chat/ChatErrorFallback';
import { AdminDashboard } from './routes/admin/index';
import { AdminUsersPage } from './routes/admin/users';
import { AdminUserDetailPage } from './routes/admin/user-detail';
import { AdminSubscriptionsPage } from './routes/admin/subscriptions';
import { AdminInvoicesPage } from './routes/admin/invoices';

// Root route with layout
const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: RouteErrorFallback,
});

// Landing page at / (public)
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
});

// Privacy page (public)
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PrivacyPage,
});

// Terms page (public)
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsPage,
});

// Pricing page (public)
const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: PricingPage,
});

// Account deletion page (public, requires auth to act)
const deleteAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/account/delete',
  component: DeleteAccountPage,
});

// Auth route (login/signup)
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  component: AuthPage,
});

// SSO callback route (no app layout)
const ssoCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/sso-callback',
  component: SSOCallbackPage,
});

// App layout route (authenticated) - all app routes under /app
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  component: AppLayout,
});

// Dashboard/Home route (new chat) - /app
const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: HomePage,
});

// Chat route - /app/c/:chatId
// Search params: pending=true indicates we should trigger AI response on load
const chatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/c/$chatId',
  component: ChatPage,
  errorComponent: ChatErrorFallback,
  validateSearch: (search: Record<string, unknown>): { pending: boolean; messageId?: string } => ({
    pending: search.pending === 'true' || search.pending === true,
    messageId: typeof search.messageId === 'string' ? search.messageId : undefined,
  }),
});

// Notes route - /app/notes
const notesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notes',
  component: NotesPage,
  validateSearch: (search: Record<string, unknown>): { noteId?: string } => ({
    noteId: typeof search.noteId === 'string' ? search.noteId : undefined,
  }),
});

// Prompts route - /app/prompts
const promptsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/prompts',
  component: PromptsPage,
});

// Billing settings route - /app/billing
const billingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/billing',
  component: BillingPage,
});

// Admin layout route - /app/admin
const adminLayoutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin',
  component: AdminLayout,
});

// Admin dashboard route - /app/admin/
const adminDashboardRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/',
  component: AdminDashboard,
});

// Admin users route - /app/admin/users
const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/users',
  component: AdminUsersPage,
});

// Admin user detail route - /app/admin/users/$userId
const adminUserDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/users/$userId',
  component: AdminUserDetailPage,
});

// Admin subscriptions route - /app/admin/subscriptions
const adminSubscriptionsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/subscriptions',
  component: AdminSubscriptionsPage,
});

// Admin invoices route - /app/admin/invoices
const adminInvoicesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/invoices',
  component: AdminInvoicesPage,
});

// Build the route tree
export const routeTree = rootRoute.addChildren([
  landingRoute,
  privacyRoute,
  termsRoute,
  pricingRoute,
  deleteAccountRoute,
  authRoute,
  ssoCallbackRoute,
  appRoute.addChildren([
    homeRoute,
    chatRoute,
    notesRoute,
    promptsRoute,
    billingRoute,
    adminLayoutRoute.addChildren([
      adminDashboardRoute,
      adminUsersRoute,
      adminUserDetailRoute,
      adminSubscriptionsRoute,
      adminInvoicesRoute,
    ]),
  ]),
]);
